(function () {
  "use strict";

  const FOCUSABLE_SELECTOR =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, iframe, [tabindex]:not([tabindex="-1"])';
  const BLOCKING_ROOT_SELECTOR =
    '[aria-hidden="true"], [data-a11y-offscreen="true"], [hidden], [inert]';
  const ORIGINAL_TABINDEX_ATTR = "data-a11y-original-tabindex";
  const ORIGINAL_ARIA_HIDDEN_ATTR = "data-a11y-original-aria-hidden";
  const SUPPORTS_INERT = "inert" in HTMLElement.prototype;
  const MOBILE_NAV_SELECTOR = "[data-mobile-nav]";
  const WISHLIST_DRAWER_SELECTOR = "[data-wishlist-drawer]";
  const TOAST_SELECTOR = ".wl-toast";
  const THIRD_PARTY_OVERLAY_SELECTOR =
    "#reamaze-popup, [id^='reamaze'], [class*='reamaze-widget'], [class*='Reamaze']";
  const JUDGEME_ROOT_SELECTOR = ".jdgm-widget, #judgeme_product_reviews";
  const RAIL_SELECTOR =
    "[data-recently-viewed-container], [data-trending-container], .flickity-slider";
  const railObservers = new WeakMap();

  // Live caches of elements that match expensive selectors. Maintained by the
  // MutationObserver so the per-frame sync functions don't have to re-run
  // `document.querySelectorAll(...)` (which for `[id^=...]` / `[class*=...]`
  // forces a full DOM walk every rAF tick).
  const overlayElements = new Set();
  const judgeMeRoots = new Set();
  // Roots that have received a childList mutation (new content) and still need
  // a cleanup pass. Attribute-only mutations do not add untreated descendants,
  // so they don't push anything onto this set — that keeps JudgeMe's constant
  // class/hover/focus churn from re-running the cleanup loop every frame.
  const dirtyJudgeMeRoots = new Set();
  // Per-node idempotency guards so repeat cleanups don't re-touch nodes that
  // were already flattened, labelled, or hidden on a previous pass.
  const flattenedJdgmControls = new WeakSet();
  const labeledJdgmControls = new WeakSet();
  const labeledJdgmFilterRows = new WeakSet();
  const hiddenJdgmIcons = new WeakSet();
  const overlayVisibilityState = new WeakMap();
  const railContainers = new Set();
  const toastElements = new Set();
  let mobileNavElement = null;
  let wishlistDrawerElement = null;

  function dropThirdPartyOverlay(overlay) {
    overlayElements.delete(overlay);
    overlayVisibilityState.delete(overlay);
  }

  function getThirdPartyOverlayRoot(element) {
    if (!(element instanceof Element)) return null;

    let overlay = element.matches(THIRD_PARTY_OVERLAY_SELECTOR)
      ? element
      : element.closest(THIRD_PARTY_OVERLAY_SELECTOR);

    if (!(overlay instanceof Element)) return null;

    // Reamaze markup often nests matching ids/classes under the real overlay
    // container. Canonicalize to the outermost matching root so the sync loop
    // tracks each widget once instead of walking every descendant.
    let parent = overlay.parentElement;
    while (parent) {
      if (parent.matches(THIRD_PARTY_OVERLAY_SELECTOR)) {
        overlay = parent;
      }
      parent = parent.parentElement;
    }

    return overlay;
  }

  function rememberThirdPartyOverlay(element) {
    const overlay = getThirdPartyOverlayRoot(element);
    if (!(overlay instanceof Element)) return null;

    overlayElements.forEach((existing) => {
      if (existing !== overlay && overlay.contains(existing)) {
        dropThirdPartyOverlay(existing);
      }
    });

    overlayElements.add(overlay);
    return overlay;
  }

  function rememberThirdPartyOverlaysInSubtree(element) {
    if (!(element instanceof Element)) return false;

    let found = false;
    if (rememberThirdPartyOverlay(element)) {
      found = true;
    }

    element.querySelectorAll(THIRD_PARTY_OVERLAY_SELECTOR).forEach((overlay) => {
      if (rememberThirdPartyOverlay(overlay)) {
        found = true;
      }
    });

    return found;
  }

  function shouldSyncThirdPartyOverlay(target, attributeName) {
    const overlay = rememberThirdPartyOverlay(target);
    if (!(overlay instanceof Element)) return false;

    if (!attributeName) return true;
    if (target === overlay) return true;

    // Descendant class churn inside the overlay is the dominant hot path in
    // the profile. Preserve syncs for structural or explicit visibility state
    // changes, but ignore descendant-only class flips.
    return (
      attributeName === "aria-hidden" ||
      attributeName === "hidden" ||
      attributeName === "inert" ||
      attributeName === "data-open"
    );
  }

  function hasOriginalTabindex(element) {
    return element.hasAttribute(ORIGINAL_TABINDEX_ATTR);
  }

  function hasOriginalAriaHidden(element) {
    return element.hasAttribute(ORIGINAL_ARIA_HIDDEN_ATTR);
  }

  function rememberTabindex(element) {
    if (hasOriginalTabindex(element)) return;
    if (element.hasAttribute("tabindex") && element.getAttribute("tabindex") === "-1") {
      return;
    }
    if (!element.hasAttribute("tabindex") && element.tabIndex < 0) {
      return;
    }
    element.setAttribute(
      ORIGINAL_TABINDEX_ATTR,
      element.hasAttribute("tabindex") ? element.getAttribute("tabindex") : "__none__",
    );
  }

  function rememberAriaHidden(element) {
    if (hasOriginalAriaHidden(element)) return;
    element.setAttribute(
      ORIGINAL_ARIA_HIDDEN_ATTR,
      element.hasAttribute("aria-hidden") ? element.getAttribute("aria-hidden") : "__none__",
    );
  }

  function restoreTabindex(element) {
    if (!hasOriginalTabindex(element)) return;
    const original = element.getAttribute(ORIGINAL_TABINDEX_ATTR);
    element.removeAttribute(ORIGINAL_TABINDEX_ATTR);
    if (original === "__none__") {
      element.removeAttribute("tabindex");
    } else {
      element.setAttribute("tabindex", original);
    }
  }

  function restoreAriaHidden(element) {
    if (!hasOriginalAriaHidden(element)) return;
    const original = element.getAttribute(ORIGINAL_ARIA_HIDDEN_ATTR);
    element.removeAttribute(ORIGINAL_ARIA_HIDDEN_ATTR);
    if (original === "__none__") {
      element.removeAttribute("aria-hidden");
    } else {
      element.setAttribute("aria-hidden", original);
    }
  }

  function setManagedTabindexOnDescendants(element, shouldDisable) {
    if (shouldDisable) {
      element.querySelectorAll(FOCUSABLE_SELECTOR).forEach((descendant) => {
        rememberTabindex(descendant);
        descendant.setAttribute("tabindex", "-1");
      });
      return;
    }

    element.querySelectorAll(`[${ORIGINAL_TABINDEX_ATTR}]`).forEach((descendant) => {
      restoreTabindex(descendant);
    });
  }

  function setManagedHidden(element, shouldHide) {
    const isManagedHidden = element.getAttribute("data-a11y-offscreen") === "true";

    if (shouldHide && isManagedHidden && element.getAttribute("aria-hidden") === "true") {
      return false;
    }
    if (!shouldHide && !isManagedHidden) {
      return false;
    }

    if (shouldHide) {
      rememberAriaHidden(element);
      element.setAttribute("aria-hidden", "true");
      element.setAttribute("data-a11y-offscreen", "true");
      // Use tabindex management instead of the `inert` attribute. `inert`
      // blocks pointer events on the entire subtree, which silently killed
      // clicks on the still-visible portion of partially-off-screen rail
      // items (e.g. restock buttons in the "Recommended For You" rail).
      // tabindex=-1 on focusables preserves keyboard a11y without breaking
      // mouse/touch interactivity.
      setManagedTabindexOnDescendants(element, true);
      return true;
    }

    element.removeAttribute("data-a11y-offscreen");
    restoreAriaHidden(element);
    setManagedTabindexOnDescendants(element, false);
    return true;
  }

  function isBlockedByAncestor(element) {
    let current = element;
    while (current && current !== document.documentElement) {
      if (
        current.hidden ||
        current.inert ||
        current.getAttribute("data-a11y-offscreen") === "true" ||
        current.getAttribute("aria-hidden") === "true"
      ) {
        return true;
      }
      current = current.parentElement;
    }

    return false;
  }

  function restoreManagedFocusableElements() {
    document.querySelectorAll(`[${ORIGINAL_TABINDEX_ATTR}]`).forEach((element) => {
      restoreTabindex(element);
    });
  }

  function syncFocusableElements() {
    restoreManagedFocusableElements();

    if (SUPPORTS_INERT) return;

    document.querySelectorAll(BLOCKING_ROOT_SELECTOR).forEach((root) => {
      if (!(root instanceof HTMLElement)) return;

      root.querySelectorAll(FOCUSABLE_SELECTOR).forEach((element) => {
        if (!isBlockedByAncestor(element)) return;

        rememberTabindex(element);
        element.setAttribute("tabindex", "-1");
      });
    });
  }

  function isElementVisiblyOpen(element) {
    if (!element || element.hidden) return false;
    if (element.getAttribute("aria-hidden") === "true") return false;
    if (SUPPORTS_INERT && element.inert) return false;

    const style = window.getComputedStyle(element);
    if (style.display === "none" || style.visibility === "hidden") return false;

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function setAttributeIfChanged(element, name, value) {
    if (element.getAttribute(name) !== value) {
      element.setAttribute(name, value);
    }
  }

  function syncMobileNav() {
    const nav =
      mobileNavElement && mobileNavElement.isConnected
        ? mobileNavElement
        : document.querySelector(MOBILE_NAV_SELECTOR);
    if (!nav) return;
    mobileNavElement = nav;

    const isOpen = nav.dataset.open === "true";
    setAttributeIfChanged(nav, "aria-hidden", isOpen ? "false" : "true");
    if (SUPPORTS_INERT && nav.inert !== !isOpen) {
      nav.inert = !isOpen;
    }
  }

  function syncWishlistDrawer() {
    const drawer =
      wishlistDrawerElement && wishlistDrawerElement.isConnected
        ? wishlistDrawerElement
        : document.querySelector(WISHLIST_DRAWER_SELECTOR);
    if (!drawer) return;
    wishlistDrawerElement = drawer;

    const isOpen = drawer.getAttribute("aria-hidden") === "false";
    if (SUPPORTS_INERT && drawer.inert !== !isOpen) {
      drawer.inert = !isOpen;
    }
  }

  function syncThemeToasts() {
    if (toastElements.size === 0) return;

    toastElements.forEach((toast) => {
      if (!toast.isConnected) {
        toastElements.delete(toast);
        return;
      }

      const isVisible = !toast.hidden && toast.getAttribute("aria-hidden") !== "true";
      const closeButton = toast.querySelector(".wl-toast-close");

      if (closeButton) {
        setAttributeIfChanged(closeButton, "tabindex", isVisible ? "0" : "-1");
        setAttributeIfChanged(closeButton, "aria-hidden", isVisible ? "false" : "true");
      }
    });
  }

  function syncThirdPartyOverlays() {
    if (overlayElements.size === 0) return;

    const pendingUpdates = [];
    overlayElements.forEach((overlay) => {
      if (!overlay.isConnected) {
        dropThirdPartyOverlay(overlay);
        return;
      }

      pendingUpdates.push([overlay, isElementVisiblyOpen(overlay)]);
    });

    pendingUpdates.forEach(([overlay, isOpen]) => {
      const nextAriaHidden = isOpen ? "false" : "true";
      const lastOpen = overlayVisibilityState.get(overlay);

      if (
        lastOpen === isOpen &&
        overlay.getAttribute("aria-hidden") === nextAriaHidden &&
        (!SUPPORTS_INERT || overlay.inert === !isOpen)
      ) {
        return;
      }

      overlayVisibilityState.set(overlay, isOpen);
      setAttributeIfChanged(overlay, "aria-hidden", nextAriaHidden);
      if (SUPPORTS_INERT && overlay.inert !== !isOpen) {
        overlay.inert = !isOpen;
      }
    });
  }

  function flattenInteractiveDescendants(root) {
    root
      .querySelectorAll("a[href], button, [role='button']")
      .forEach((control) => {
        if (flattenedJdgmControls.has(control)) return;
        flattenedJdgmControls.add(control);
        control.querySelectorAll(FOCUSABLE_SELECTOR).forEach((descendant) => {
          if (descendant === control) return;
          descendant.setAttribute("tabindex", "-1");
          descendant.setAttribute("aria-hidden", "true");
          descendant.style.pointerEvents = "none";
        });
      });
  }

  function cleanedLabel(text) {
    return (text || "")
      .replace(/[\uE000-\uF8FF]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function labelJudgeMeControls(root) {
    root.querySelectorAll("a[href], button").forEach((control) => {
      if (labeledJdgmControls.has(control)) return;
      if (control.hasAttribute("aria-label")) {
        labeledJdgmControls.add(control);
        return;
      }

      const text = cleanedLabel(control.textContent);
      if (text) {
        control.setAttribute("aria-label", text);
        labeledJdgmControls.add(control);
      }
    });

    root.querySelectorAll(".jdgm-sort-dropdown").forEach((select) => {
      if (labeledJdgmControls.has(select)) return;
      if (!select.hasAttribute("aria-label")) {
        select.setAttribute("aria-label", "Sort reviews");
      }
      labeledJdgmControls.add(select);
    });

    root.querySelectorAll(".jdgm-rev__share-btn").forEach((button) => {
      if (labeledJdgmControls.has(button)) return;
      button.setAttribute("aria-label", "Share review");
      labeledJdgmControls.add(button);
    });

    root.querySelectorAll(".jdgm-rev__thumb-btn").forEach((button) => {
      if (labeledJdgmControls.has(button)) return;
      button.setAttribute("aria-label", "Mark review as helpful");
      labeledJdgmControls.add(button);
    });

    root.querySelectorAll(".jdgm-histogram__row").forEach((row) => {
      if (labeledJdgmFilterRows.has(row)) return;
      const starLabel = cleanedLabel(
        row.querySelector(".jdgm-histogram__star")?.textContent || "",
      );
      const filterLabel = starLabel
        ? `Filter ${starLabel} reviews`
        : "Filter reviews";

      row.querySelectorAll("a[href], button").forEach((control) => {
        if (!control.hasAttribute("aria-label")) {
          control.setAttribute("aria-label", filterLabel);
        }
        labeledJdgmControls.add(control);
      });
      labeledJdgmFilterRows.add(row);
    });
  }

  function hideDecorativeJudgeMeIcons(root) {
    root
      .querySelectorAll(
        ".jdgm-sort-dropdown-arrow, .jdgm-verified-checkmark, .jdgm-medals-wrapper, .jdgm-rev__icon, .jdgm-star, .fi",
      )
      .forEach((icon) => {
        if (hiddenJdgmIcons.has(icon)) return;
        icon.setAttribute("aria-hidden", "true");
        icon.setAttribute("focusable", "false");
        hiddenJdgmIcons.add(icon);
      });
  }

  function cleanupJudgeMe() {
    if (judgeMeRoots.size === 0) return;
    if (dirtyJudgeMeRoots.size === 0) return;

    const roots = Array.from(dirtyJudgeMeRoots);
    dirtyJudgeMeRoots.clear();

    roots.forEach((widget) => {
      if (!widget.isConnected) {
        judgeMeRoots.delete(widget);
        return;
      }

      flattenInteractiveDescendants(widget);
      labelJudgeMeControls(widget);
      hideDecorativeJudgeMeIcons(widget);
    });
  }

  function getRailRoot(container) {
    if (container.matches(".flickity-slider")) {
      return container.closest(".flickity-viewport");
    }

    return container;
  }

  function getRailItems(container) {
    return Array.from(container.children).filter((child) => child instanceof HTMLElement);
  }

  function computeIntersectionRatio(itemRect, rootRect) {
    const left = Math.max(itemRect.left, rootRect.left);
    const right = Math.min(itemRect.right, rootRect.right);
    const width = Math.max(0, right - left);
    if (itemRect.width <= 0) return 0;
    return width / itemRect.width;
  }

  function syncRailVisibility(container) {
    const root = getRailRoot(container);
    if (!root) return;

    const rootRect = root.getBoundingClientRect();
    getRailItems(container).forEach((item) => {
      const itemRect = item.getBoundingClientRect();
      const intersectionRatio = computeIntersectionRatio(itemRect, rootRect);
      setManagedHidden(item, intersectionRatio < 0.55);
    });
  }

  function ensureScrollableRail(container) {
    const root = getRailRoot(container);
    if (!root) return;

    const items = getRailItems(container);
    if (items.length === 0) return;

    const existing = railObservers.get(container);
    const isUnchanged =
      existing &&
      existing.root === root &&
      existing.items.length === items.length &&
      existing.items.every((item, index) => item === items[index]);

    if (isUnchanged) {
      if (!existing.observer) {
        syncRailVisibility(container);
      }
      return;
    }

    if (existing?.observer) {
      existing.observer.disconnect();
    }

    if (!("IntersectionObserver" in window)) {
      railObservers.set(container, { observer: null, root, items });
      syncRailVisibility(container);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        let changed = false;
        entries.forEach((entry) => {
          if (!(entry.target instanceof HTMLElement)) return;
          const shouldHide = !(entry.isIntersecting && entry.intersectionRatio >= 0.55);
          changed = setManagedHidden(entry.target, shouldHide) || changed;
        });
        if (changed && !SUPPORTS_INERT) {
          markPendingSync("focusables");
          queueSync();
        }
      },
      {
        root,
        threshold: [0, 0.55, 1],
      },
    );

    items.forEach((item) => observer.observe(item));
    railObservers.set(container, { observer, root, items });
    syncRailVisibility(container);
  }

  function syncScrollableRails() {
    if (railContainers.size === 0) return;

    railContainers.forEach((container) => {
      if (!container.isConnected) {
        railContainers.delete(container);
        return;
      }
      ensureScrollableRail(container);
    });
  }

  function seedCachedRoots() {
    mobileNavElement = document.querySelector(MOBILE_NAV_SELECTOR);
    wishlistDrawerElement = document.querySelector(WISHLIST_DRAWER_SELECTOR);

    document
      .querySelectorAll(TOAST_SELECTOR)
      .forEach((toast) => toastElements.add(toast));
    const seedRoot = document.body || document.documentElement;
    if (seedRoot) {
      rememberThirdPartyOverlaysInSubtree(seedRoot);
    }
    document
      .querySelectorAll(JUDGEME_ROOT_SELECTOR)
      .forEach((widget) => {
        judgeMeRoots.add(widget);
        dirtyJudgeMeRoots.add(widget);
      });
    document.querySelectorAll(RAIL_SELECTOR).forEach((container) => {
      if (container instanceof HTMLElement) railContainers.add(container);
    });
  }

  function syncAccessibilityState() {
    syncMobileNav();
    syncWishlistDrawer();
    syncThemeToasts();
    syncThirdPartyOverlays();
    syncScrollableRails();
    // First-run cleanup must process any known roots, so seed the dirty set
    // from whatever was discovered during seedCachedRoots. Subsequent passes
    // are driven by childList mutations only.
    if (dirtyJudgeMeRoots.size === 0 && judgeMeRoots.size > 0) {
      judgeMeRoots.forEach((widget) => dirtyJudgeMeRoots.add(widget));
    }
    cleanupJudgeMe();
    syncFocusableElements();
  }

  function initSkipLink() {
    const skipLink = document.querySelector(".skip-to-main");
    const main = document.getElementById("site-main");

    if (!(skipLink instanceof HTMLAnchorElement) || !(main instanceof HTMLElement)) {
      return;
    }

    skipLink.addEventListener("click", () => {
      window.requestAnimationFrame(() => {
        main.focus({ preventScroll: true });
      });
    });
  }

  let rafId = null;
  let pendingSyncState = createPendingSyncState();

  function createPendingSyncState() {
    return {
      mobileNav: false,
      wishlistDrawer: false,
      toasts: false,
      thirdPartyOverlays: false,
      rails: false,
      judgeMe: false,
      focusables: false,
    };
  }

  function hasPendingSyncs() {
    return (
      pendingSyncState.mobileNav ||
      pendingSyncState.wishlistDrawer ||
      pendingSyncState.toasts ||
      pendingSyncState.thirdPartyOverlays ||
      pendingSyncState.rails ||
      pendingSyncState.judgeMe ||
      pendingSyncState.focusables
    );
  }

  function allFlagsPending() {
    return (
      pendingSyncState.mobileNav &&
      pendingSyncState.wishlistDrawer &&
      pendingSyncState.toasts &&
      pendingSyncState.thirdPartyOverlays &&
      pendingSyncState.rails &&
      pendingSyncState.judgeMe &&
      (SUPPORTS_INERT || pendingSyncState.focusables)
    );
  }

  function markPendingSync(key) {
    pendingSyncState[key] = true;
  }

  function queueAllSyncs() {
    pendingSyncState = {
      mobileNav: true,
      wishlistDrawer: true,
      toasts: true,
      thirdPartyOverlays: true,
      rails: true,
      judgeMe: true,
      focusables: true,
    };
    // Section reloads may swap DOM — treat every known JudgeMe root as dirty
    // so cleanupJudgeMe processes them once. Subsequent frames won't run
    // cleanup unless a fresh childList mutation lands inside a root.
    judgeMeRoots.forEach((widget) => dirtyJudgeMeRoots.add(widget));
    queueSync();
  }

  // Fast path for ATTRIBUTE mutations: an attribute change on `target` cannot
  // introduce or remove descendants, so we only need ancestor-direction checks
  // (`closest()` / `matches()`). The previous implementation ran a full
  // `element.querySelector(subtreeSelector)` for each of six selectors on every
  // attribute mutation — Flickity / third-party widgets flip classes many times
  // per second, so this dominated the profile.
  function collectAttributeSyncNeeds(target, attributeName) {
    if (!(target instanceof Element)) return;

    if (!pendingSyncState.mobileNav && target.closest(MOBILE_NAV_SELECTOR)) {
      markPendingSync("mobileNav");
    }

    if (
      !pendingSyncState.wishlistDrawer &&
      target.closest(WISHLIST_DRAWER_SELECTOR)
    ) {
      markPendingSync("wishlistDrawer");
    }

    if (!pendingSyncState.toasts && target.closest(TOAST_SELECTOR)) {
      markPendingSync("toasts");
    }

    if (
      !pendingSyncState.thirdPartyOverlays &&
      shouldSyncThirdPartyOverlay(target, attributeName)
    ) {
      markPendingSync("thirdPartyOverlays");
    }

    if (!pendingSyncState.rails && target.closest(RAIL_SELECTOR)) {
      markPendingSync("rails");
    }

    // JudgeMe cleanup is NOT flagged here. This code path runs for every
    // attribute mutation (hover, focus, open/close dropdowns, flickity flips
    // classes many times a second), and attribute churn never introduces
    // untreated descendants — so cleaning again is pure waste. Cleanup is
    // scheduled only on real childList additions inside a JudgeMe root via
    // `markJudgeMeDirty` / `registerAddedElement`.

    if (SUPPORTS_INERT || pendingSyncState.focusables) return;

    if (
      target.matches(BLOCKING_ROOT_SELECTOR) ||
      target.matches(FOCUSABLE_SELECTOR) ||
      target.closest(BLOCKING_ROOT_SELECTOR)
    ) {
      markPendingSync("focusables");
    }
  }

  // Used for added subtrees on childList mutations. A single `querySelectorAll`
  // per relevant selector is acceptable here because it only runs when new DOM
  // is inserted, not on every attribute flip. We also seed the live caches
  // (overlayElements, judgeMeRoots, toastElements, railContainers) so the
  // per-frame syncs can iterate the set directly.
  function registerAddedElement(element) {
    if (!(element instanceof Element)) return;

    if (!mobileNavElement && element.matches(MOBILE_NAV_SELECTOR)) {
      mobileNavElement = element;
      markPendingSync("mobileNav");
    } else if (!mobileNavElement) {
      const found = element.querySelector(MOBILE_NAV_SELECTOR);
      if (found) {
        mobileNavElement = found;
        markPendingSync("mobileNav");
      }
    } else if (!pendingSyncState.mobileNav && element.contains(mobileNavElement)) {
      markPendingSync("mobileNav");
    }

    if (!wishlistDrawerElement && element.matches(WISHLIST_DRAWER_SELECTOR)) {
      wishlistDrawerElement = element;
      markPendingSync("wishlistDrawer");
    } else if (!wishlistDrawerElement) {
      const found = element.querySelector(WISHLIST_DRAWER_SELECTOR);
      if (found) {
        wishlistDrawerElement = found;
        markPendingSync("wishlistDrawer");
      }
    }

    if (element.matches(TOAST_SELECTOR)) {
      toastElements.add(element);
      markPendingSync("toasts");
    } else {
      const toastMatches = element.querySelectorAll(TOAST_SELECTOR);
      if (toastMatches.length) {
        toastMatches.forEach((toast) => toastElements.add(toast));
        markPendingSync("toasts");
      }
    }

    if (rememberThirdPartyOverlaysInSubtree(element)) {
      markPendingSync("thirdPartyOverlays");
    }

    if (element.matches(RAIL_SELECTOR)) {
      if (element instanceof HTMLElement) railContainers.add(element);
      markPendingSync("rails");
    } else {
      const railMatches = element.querySelectorAll(RAIL_SELECTOR);
      if (railMatches.length) {
        railMatches.forEach((container) => {
          if (container instanceof HTMLElement) railContainers.add(container);
        });
        markPendingSync("rails");
      }
    }

    if (element.matches(JUDGEME_ROOT_SELECTOR)) {
      judgeMeRoots.add(element);
      dirtyJudgeMeRoots.add(element);
      markPendingSync("judgeMe");
    } else {
      const judgeMeMatches = element.querySelectorAll(JUDGEME_ROOT_SELECTOR);
      if (judgeMeMatches.length) {
        judgeMeMatches.forEach((widget) => {
          judgeMeRoots.add(widget);
          dirtyJudgeMeRoots.add(widget);
        });
        markPendingSync("judgeMe");
      }
    }

    // Added subtree that landed inside an existing JudgeMe root.
    if (!pendingSyncState.judgeMe || dirtyJudgeMeRoots.size === 0) {
      const enclosingRoot = element.closest(JUDGEME_ROOT_SELECTOR);
      if (enclosingRoot && judgeMeRoots.has(enclosingRoot)) {
        dirtyJudgeMeRoots.add(enclosingRoot);
        markPendingSync("judgeMe");
      }
    }

    if (SUPPORTS_INERT || pendingSyncState.focusables) return;

    if (
      element.matches(BLOCKING_ROOT_SELECTOR) ||
      element.matches(FOCUSABLE_SELECTOR) ||
      element.closest(BLOCKING_ROOT_SELECTOR) ||
      element.querySelector(BLOCKING_ROOT_SELECTOR) ||
      element.querySelector(FOCUSABLE_SELECTOR)
    ) {
      markPendingSync("focusables");
    }
  }

  function collectMutationSyncNeeds(mutation) {
    if (allFlagsPending()) return;

    if (mutation.type === "attributes") {
      collectAttributeSyncNeeds(mutation.target, mutation.attributeName);
      return;
    }

    if (mutation.type !== "childList") {
      return;
    }

    // Container-level impact (e.g., rail re-layouts, judgeme re-renders).
    const target = mutation.target;
    if (target instanceof Element) {
      collectAttributeSyncNeeds(target);

      // When new children are injected into an existing JudgeMe root (the
      // attribute path deliberately ignores JudgeMe), flag that specific root
      // dirty so the next cleanup pass processes only the affected widget.
      if (mutation.addedNodes && mutation.addedNodes.length) {
        const enclosingRoot = target.closest(JUDGEME_ROOT_SELECTOR);
        if (enclosingRoot && judgeMeRoots.has(enclosingRoot)) {
          dirtyJudgeMeRoots.add(enclosingRoot);
          markPendingSync("judgeMe");
        }
      }
    }

    const added = mutation.addedNodes;
    if (added && added.length) {
      for (let i = 0; i < added.length; i++) {
        const node = added[i];
        if (node instanceof Element) {
          registerAddedElement(node);
        } else if (node && node.parentElement) {
          collectAttributeSyncNeeds(node.parentElement);
        }
        if (allFlagsPending()) return;
      }
    }
  }

  function queueSync() {
    if (rafId) return;
    rafId = window.requestAnimationFrame(() => {
      rafId = null;

      const nextSync = pendingSyncState;
      pendingSyncState = createPendingSyncState();

      if (nextSync.mobileNav) {
        syncMobileNav();
      }
      if (nextSync.wishlistDrawer) {
        syncWishlistDrawer();
      }
      if (nextSync.toasts) {
        syncThemeToasts();
      }
      if (nextSync.thirdPartyOverlays) {
        syncThirdPartyOverlays();
      }
      if (nextSync.rails) {
        syncScrollableRails();
      }
      if (nextSync.judgeMe) {
        cleanupJudgeMe();
      }
      if (nextSync.focusables) {
        syncFocusableElements();
      }
    });
  }

  function init() {
    initSkipLink();
    seedCachedRoots();
    syncAccessibilityState();

    const observer = new MutationObserver((mutations) => {
      for (let i = 0; i < mutations.length; i++) {
        collectMutationSyncNeeds(mutations[i]);
        if (allFlagsPending()) break;
      }
      if (hasPendingSyncs()) {
        queueSync();
      }
    });

    observer.observe(document.body || document.documentElement, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: [
        "aria-hidden",
        "hidden",
        "class",
        "data-open",
        "inert",
      ],
    });

    document.addEventListener("shopify:section:load", queueAllSyncs);
    document.addEventListener("shopify:block:select", queueAllSyncs);
    document.addEventListener("shopify:block:deselect", queueAllSyncs);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
